import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";

const Tile = ({ label, value, hint, testid, tone = "default" }) => {
  const toneClass =
    tone === "warn" ? "text-amber-600 dark:text-amber-400" :
    tone === "alert" ? "text-red-600 dark:text-red-400" :
    tone === "ok" ? "text-emerald-600 dark:text-emerald-400" : "";
  return (
    <div className="p-5 border border-border" data-testid={testid}>
      <div className="font-overline text-muted-foreground text-[11px]">{label}</div>
      <div className={`font-mono-data text-3xl mt-2 font-light ${toneClass}`}>{value ?? "—"}</div>
      {hint && <div className="text-[10px] text-muted-foreground mt-1 font-mono uppercase tracking-wider">{hint}</div>}
    </div>
  );
};

export default function OverviewTab({ onJump }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let live = true;
    const load = () => {
      api.get("/admin/overview")
        .then((r) => { if (live) setData(r.data); })
        .catch((e) => { if (live) setErr(e?.response?.data?.detail || "Failed to load overview"); });
    };
    load();
    const t = setInterval(load, 30000); // gentle 30s poll
    return () => { live = false; clearInterval(t); };
  }, []);

  if (err) return <div className="border border-border p-6 text-red-500 font-mono text-sm" data-testid="overview-error">{err}</div>;
  if (!data) return <div className="border border-border p-10 text-center text-muted-foreground" data-testid="overview-loading">Loading overview…</div>;

  const d = data.deals || {};
  const l = data.listings || {};
  const de = data.dealers || {};
  const u = data.users || {};

  return (
    <div className="space-y-8" data-testid="admin-overview">
      <section>
        <div className="font-overline text-accent mb-3">Transactions</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-l border-t border-border">
          <Tile testid="tile-active-deals"     label="Active Deals" value={d.active} hint="Open workflow" />
          <Tile testid="tile-funding-pending"  label="Funding Pending" value={d.funding_pending} tone="warn" />
          <Tile testid="tile-funds-received"   label="Funds Received" value={d.funds_received_pending_clearance} hint="Awaiting clearance" />
          <Tile testid="tile-funds-cleared"    label="Cleared, Not Settled" value={d.funds_cleared_awaiting_settlement} tone="warn" />
          <Tile testid="tile-ready-settlement" label="Ready for Settlement" value={d.ready_for_settlement} tone="ok" />
          <Tile testid="tile-open-disputes"    label="Open Disputes" value={d.open_disputes} tone={d.open_disputes > 0 ? "alert" : "default"} />
          <Tile testid="tile-closed-deals"     label="Closed Deals" value={d.closed} />
        </div>
      </section>

      <section>
        <div className="font-overline text-accent mb-3">Marketplace</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-l border-t border-border">
          <Tile testid="tile-active-listings"    label="Active Listings" value={l.active} />
          <Tile testid="tile-pending-listings"   label="Pending Listing Reviews" value={l.pending_review} tone={l.pending_review > 0 ? "warn" : "default"} />
          <Tile testid="tile-pending-dealers"    label="Pending Dealer Verifications" value={de.pending_verification} tone={de.pending_verification > 0 ? "warn" : "default"} />
          <Tile testid="tile-featured-active"    label="Featured Listings Active" value={data.featured?.active} />
          <Tile testid="tile-approved-dealers"   label="Approved Dealers" value={de.approved} />
          <Tile testid="tile-pending-documents"  label="Documents Awaiting Review" value={data.documents?.pending_review} tone={data.documents?.pending_review > 0 ? "warn" : "default"} />
        </div>
      </section>

      <section>
        <div className="font-overline text-accent mb-3">Accounts</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-0 border-l border-t border-border">
          <Tile testid="tile-users-total"     label="Users Total" value={u.total} />
          <Tile testid="tile-users-admins"    label="Admins" value={u.admins} />
          <Tile testid="tile-users-dealers"   label="Dealer Accounts" value={u.dealers} />
          <Tile testid="tile-users-suspended" label="Suspended" value={u.suspended} tone={u.suspended > 0 ? "alert" : "default"} />
          <Tile testid="tile-users-unverified" label="Unverified" value={u.unverified} />
        </div>
      </section>

      <section>
        <div className="flex items-center justify-between mb-3">
          <div className="font-overline text-accent">Recent Audit Activity</div>
          <div className="flex gap-3">
            <Link to="/admin/impersonation-logs" className="font-overline text-xs text-muted-foreground hover:text-primary" data-testid="link-impersonation-logs">
              Impersonation Logs →
            </Link>
            {onJump && (
              <button onClick={() => onJump("audit")} className="font-overline text-xs text-primary hover:underline" data-testid="link-full-audit">
                Full Audit Log →
              </button>
            )}
          </div>
        </div>
        <div className="border border-border overflow-x-auto" data-testid="overview-audit-list">
          {(data.recent_audit || []).length === 0 ? (
            <div className="p-6 text-center text-muted-foreground text-sm">No recent activity.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left">
                <tr>
                  <th className="px-3 py-2 font-overline text-[10px]">When</th>
                  <th className="px-3 py-2 font-overline text-[10px]">Event</th>
                  <th className="px-3 py-2 font-overline text-[10px]">Actor</th>
                  <th className="px-3 py-2 font-overline text-[10px]">Deal</th>
                </tr>
              </thead>
              <tbody>
                {(data.recent_audit || []).slice(0, 12).map((r, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="px-3 py-2 font-mono text-[11px] whitespace-nowrap">
                      {r.at ? new Date(r.at).toLocaleString() : "—"}
                    </td>
                    <td className="px-3 py-2 font-mono text-[11px] uppercase">{r.event}</td>
                    <td className="px-3 py-2 font-mono text-[11px]">{r.actor_role || "—"} · {r.actor_id?.slice(-6) || "—"}</td>
                    <td className="px-3 py-2 font-mono text-[11px]">
                      {r.deal_id ? (
                        <Link to={`/deals/${r.deal_id}`} className="hover:underline">{r.deal_id.slice(-8)}</Link>
                      ) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}
